<template>
  <v-card>
    <v-card-title class="pb-0 mb-0">
      <div class="card-title">
        <span class="bold table-card-title">
          <slot name="title">Minhas campanhas</slot>
        </span>

        <div class="status-search-group">
          <mf-status-filter v-if="itemsFilter.length > 1" :items="itemsFilter" :value="itemsFilter.map((i) => i.value)" @input="getFilterResult" />
          <headers-data-table-settings
            :headers.sync="headers"
            :default-headers="getDefaultHeadersOptions()"
            :headers-settings-name="industry ? 'offer-management-industry-campaigns-table' : 'offer-management-campaigns-table'"
          />
          <div class="min-width-345">
            <mf-search v-model="search" class="pt-0 mt-0" hide-details placeholder="Busca" />
          </div>
        </div>
      </div>
    </v-card-title>
    <v-card-text class="pa-0">
      <div v-if="!isFranchisee && !isFranchise && !industry">
        <v-col class="pa-0 pl-4" cols="3" justify="end">
          <mf-toggle v-model="showAuto" label="Ofertas do ERP" class="my-0" />
        </v-col>
        <v-spacer />
      </div>
      <paginated-data-table
        ref="table"
        class="history-striped"
        :headers="computedHeaders"
        :search="search"
        :query="query"
        :custom-items-per-page="franchisee ? 5 : 20"
        :items-per-page-options="industry ? [10, 15, 20] : [5, 10, 15, 20]"
        :query-variables="queryVariables"
        :graphql-uri="$microservicesUrls['sales']"
        :default-table-sort-by="defaultTableSortBy"
        :default-table-sort-desc="defaultTableSortDesc"
        :reset-to-default-sort-when-empty="resetToDefaultSortWhenEmpty"
        reset-page-when-query-variable-change
        query-name="offerSchedules"
      >
        <template v-slot:item.status="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <mf-icon
                fill
                :size="item.status === 'processing' ? 12 : 16"
                :color="getStatusLabel(item.status).color"
                :icon="item.status === 'processing' ? 'processing' : 'fiber_manual_record'"
                :class="item.status === 'processing' ? 'mr-1' : ''"
              />
              {{ getStatusLabel(item.status).text }}
            </div>
          </div>
        </template>

        <template v-slot:item.name="{ item }" style="max-width: 300px">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <mf-tooltip v-model="showTooltipTitle[item._id]" max-width="250" top content-class="truncated-tooltip">
                <template v-slot:activator="{ on }">
                  <router-link v-if="canViewResults(item)" class="link" :to="getResultsRoute(item, franchisee)" v-on="on">
                    {{ item.name | truncate({ length: 62 }) }}
                  </router-link>
                  <router-link v-else-if="canEditCampaign(item)" class="link" :to="getEditRoute(item, franchisee)" v-on="on">
                    {{ item.name | truncate({ length: 62 }) }}
                  </router-link>
                  <span v-else v-on="on">{{ item.name | truncate({ length: 62 }) }}</span>
                </template>
                <span>{{ item.name }}</span>
              </mf-tooltip>
            </div>
          </div>
        </template>

        <template v-if="industry" v-slot:item.modality="{ item }">
          <div class="fix-td-position colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <v-chip class="modality-chip" :color="getModality(item).color" :style="{ color: getModality(item).color + ' !important' }" small label outlined>{{
              getModality(item).text
            }}</v-chip>
          </div>
        </template>

        <template v-if="!industry" v-slot:item.description="{ item }" style="max-width: 300px">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <v-tooltip v-model="showTooltipDescription[item._id]" top max-width="250" content-class="truncated-tooltip">
                <template v-slot:activator="{ on }">
                  <span v-on="on">{{ item.description | truncate({ length: 150 }) }}</span>
                </template>
                <span>{{ item.description }}</span>
              </v-tooltip>
            </div>
          </div>
        </template>

        <template v-slot:item.products_count="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              {{ item.products_count }}
            </div>
          </div>
        </template>

        <template v-slot:item.types="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <mf-tooltip top max-width="300px" :disabled="getOfferTypesTooltip(item.types).length <= 0">
                <template #activator="{ on }">
                  <span v-on="on">{{ getOfferTypes(item.types).length === 1 ? getOfferTypes(item.types)[0] : 'Múltiplos' }}</span>
                </template>
                <span>{{ getOfferTypesTooltip(item.types) }}</span>
              </mf-tooltip>
            </div>
          </div>
        </template>

        <template v-slot:item.is_personalized="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              {{ industry && item.industry.is_mercapromo ? 'Massivo' : item.segments.length ? 'Personalizado' : 'Clube' }}
            </div>
          </div>
        </template>

        <template v-if="!industry" v-slot:item.activatable="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              {{ item.activatable ? 'Ativável' : 'Livre' }}
            </div>
          </div>
        </template>

        <template v-slot:item.units="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" style="cursor: auto" v-on="on">
                    {{ getUnits(item, true) }}
                  </span>
                </template>
                <span v-for="(unit, index) in item.units" :key="index" style="display: block">
                  {{ unit.name }}
                </span>
              </v-tooltip>
            </div>
          </div>
        </template>

        <template v-if="industry" v-slot:item.industry_name="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              {{ item.industry.name }}
            </div>
          </div>
        </template>

        <template v-slot:item.start_date="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <div v-if="showTooltip(item.status)">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <span v-if="item.start_date && item.end_date" v-on="on"
                      >{{ DateTime.fromISO(item.start_date).toFormat('dd/MM/yyyy') }} - {{ DateTime.fromISO(item.end_date).toFormat('dd/MM/yyyy') }}</span
                    >
                    <span v-else v-on="on"> - </span>
                  </template>
                  <span>{{ getCampaignDuration(item) }}</span>
                </v-tooltip>
              </div>
              <div v-else>
                <span v-if="item.start_date && item.end_date">
                  {{ DateTime.fromISO(item.start_date).toFormat('dd/MM/yyyy') }} - {{ DateTime.fromISO(item.end_date).toFormat('dd/MM/yyyy') }}
                </span>
                <span v-else> - </span>
              </div>
            </div>
          </div>
        </template>

        <template v-slot:item.created_at="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <span v-if="item.created_at">
                {{ DateTime.fromISO(item.created_at).toFormat('dd/MM/yyyy HH:mm:ss') }}
              </span>
              <span v-else> - </span>
            </div>
          </div>
        </template>

        <template v-slot:item.updated_at="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <span v-if="item.updated_at">
                {{ DateTime.fromISO(item.updated_at).toFormat('dd/MM/yyyy HH:mm:ss') }}
              </span>
              <span v-else> - </span>
            </div>
          </div>
        </template>

        <template v-if="industry" v-slot:item.proposal_deadline="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <span v-if="item.industry.proposal_deadline" v-on="on">{{
                    DateTime.fromISO(item.industry.proposal_deadline).toFormat('dd/MM/yyyy HH:mm')
                  }}</span>
                  <span v-else v-on="on">-</span>
                </template>
                <span>Data limite que o varejista poderá aderir a campanha</span>
              </v-tooltip>
            </div>
          </div>
        </template>

        <template v-slot:item.menu="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <div v-if="franchisee">
                <v-menu
                  v-if="!industry && item.status !== 'processing'"
                  class="campaigns-table-actions"
                  bottom
                  origin="center center"
                  transition="scale-transition"
                >
                  <template v-slot:activator="{ on }">
                    <mf-button type="icon" icon="more_vert" color="cinza-0" v-on:click.native="on.click" />
                  </template>
                  <v-list>
                    <v-list-item
                      v-if="item.has_results && (item.status === 'active' || item.status === 'finished')"
                      data-cy="results-offer"
                      @click="redirectToResults(item, franchisee)"
                    >
                      <v-list-item-title>Resultados</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="redirectToEdit(item, franchisee)">
                      <v-list-item-title>Visualizar</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="item.has_results && (item.status === 'active' || item.status === 'finished')"
                      data-cy="sessao-acompanhamento"
                      @click="redirectToConsumedSalesSession(item._id, franchisee)"
                    >
                      <v-list-item-title>Histórico de consultas</v-list-item-title>
                    </v-list-item>
                    <export-offer-sales-modal
                      v-if="item.status !== 'processing'"
                      :offer-sales-id="item._id"
                      :is-industry-campaign="industry"
                      :is-mercapromo-campaign="industry && item.industry.is_mercapromo"
                      is-franchisee
                    />
                  </v-list>
                </v-menu>
              </div>
              <div v-if="!franchisee">
                <v-menu class="campaigns-table-actions" bottom origin="center center" transition="scale-transition">
                  <template v-slot:activator="{ on }">
                    <mf-button type="icon" icon="more_vert" color="cinza-0" v-on:click.native="on.click" />
                  </template>
                  <v-list>
                    <v-list-item :disabled="isZeDeliveryCampaign(item)" @click="redirectToEdit(item)">
                      <v-list-item-title v-if="industry && item.industry.is_mercapromo"> Visualizar </v-list-item-title>
                      <v-list-item-title
                        v-else-if="
                          (item.status === 'draft' || item.status === 'scheduled' || item.status === 'active' || item.status === 'industry_proposal') &&
                          !queryVariables.includeAuto
                        "
                        data-cy="edit-or-view-offer"
                      >
                        Editar
                      </v-list-item-title>
                      <v-list-item-title v-else> Visualizar </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="item.status === 'scheduled'"
                      :disabled="isZeDeliveryCampaign(item)"
                      @click="unscheduleCampaign(item._id, item.omni_campaigns, item.industry)"
                    >
                      <v-list-item-title>Suspender</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="item.status === 'draft' || item.status === 'industry_proposal'"
                      data-cy="delete-offer"
                      :disabled="industry"
                      @click="deleteCampaign(item._id, item.name)"
                    >
                      <v-list-item-title>Deletar</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="item.status === 'active' && !queryVariables.includeAuto"
                      :disabled="isZeDeliveryCampaign(item)"
                      @click="finishCampaign(item._id, item.omni_campaigns)"
                    >
                      <v-list-item-title>Finalizar</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="!queryVariables.includeAuto" :disabled="industry" @click="cloneCampaign(item._id)">
                      <v-list-item-title>Duplicar</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="isSomeChannelAllowed && !showAuto"
                      :disabled="(item.status !== 'scheduled' && item.status !== 'active') || industry"
                      @click="openCommunicateModal(item._id)"
                    >
                      <v-list-item-title>Comunicar</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="
                        item.has_results &&
                        (item.status === 'active' || item.status === 'finished' || item.status === 'expired' || item.status === 'industry_rejected')
                      "
                      data-cy="results-offer"
                      :disabled="industry && (item.status == 'expired' || item.status == 'industry_rejected')"
                      @click="redirectToResults(item)"
                    >
                      <v-list-item-title>Resultados</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="item.has_results && (item.status === 'active' || item.status === 'finished')"
                      data-cy="sessao-acompanhamento"
                      :disabled="industry"
                      @click="redirectToConsumedSalesSession(item._id)"
                    >
                      <v-list-item-title>Histórico de consultas</v-list-item-title>
                    </v-list-item>
                    <export-offer-sales-modal
                      v-if="item.status !== 'processing'"
                      :offer-sales-id="item._id"
                      :is-industry-campaign="industry"
                      :is-mercapromo-campaign="industry && item.industry.is_mercapromo"
                    />
                    <v-list-item @click="openLogsModal(item._id)">
                      <v-list-item-title>Logs</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
              <div v-else-if="industry && allowsViewingFranchiseTradeResults">
                <v-menu class="campaigns-table-actions" bottom origin="center center" transition="scale-transition">
                  <template v-slot:activator="{ on }">
                    <mf-button type="icon" icon="more_vert" color="cinza-0" v-on:click.native="on.click" />
                  </template>
                  <v-list>
                    <v-list-item
                      v-if="
                        item.has_results &&
                        (item.status === 'active' || item.status === 'finished' || item.status === 'expired' || item.status === 'industry_rejected')
                      "
                      data-cy="results-offer"
                      :disabled="industry && (item.status == 'expired' || item.status == 'industry_rejected')"
                      @click="redirectToResults(item, franchisee)"
                    >
                      <v-list-item-title>Resultados</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
          </div>
        </template>
      </paginated-data-table>
    </v-card-text>
    <communicate-modal v-model="communicateDialog" :offer-schedule-id="offerScheduleId" />
    <logs-modal v-model="logsDialog" tags="OFFERS" logTitle="gestão de ofertas" :idItem="offerId" />
  </v-card>
</template>

<script>
import PaginatedDataTable from '@/components/PaginatedDataTable.vue'
import LogsModal from '@/components/logs-modal/LogsModal.vue'
import CommunicateModal from '@/modules/campanhas/gestao-ofertas-v2/views/components/CommunicateModal.vue'
import ExportOfferSalesModal from '@/modules/campanhas/gestao-ofertas-v2/views/components/ExportOfferSalesModal.vue'
import {
  QUERY_LIST_SALES_CAMPAIGNS_OF_TABLE,
  MUTATION_UNSCHEDULED_SALES_CAMPAIGN,
  MUTATION_FINISH_SALES_CAMPAIGN,
  MUTATION_CLONE_SALES_CAMPAIGN,
  MUTATION_DELETE_SALES_CAMPAIGN,
} from '../../graphql'
import { DateTime } from 'luxon'
import { getChannelsPermissionsStatus, checkIfSomeChannelIsAllowed } from '@/modules/campanhas/gestao-ofertas-v2/helpers/channels'
import { getUnits, getStatusLabel } from '@/config/helpers.js'
import { industryWarnings } from '../mixins/industry-warnings'
import { getProductFormatTypeLabel } from '@/modules/mobile/ofertas/helpers/productType.js'
import HeadersDataTableSettings from '@/components/HeadersDataTableSettings.vue'
import cloneDeep from 'lodash/cloneDeep'
import { mapGetters, mapState } from 'vuex'
import getModality from '@/modules/industria/helpers/modalityHelper.js'

export default {
  name: 'CampaignsTable',
  components: {
    PaginatedDataTable,
    LogsModal,
    CommunicateModal,
    HeadersDataTableSettings,
    ExportOfferSalesModal,
  },

  filters: {
    hifen(value) {
      if (!value) return '-'
      return value
    },
  },

  mixins: [industryWarnings],

  props: {
    franchisee: {
      type: Boolean,
      default: false,
    },
    finished: {
      type: Boolean,
      default: false,
    },
    units: {
      type: Array,
    },
    industry: {
      type: Boolean,
      default: false,
    },
    unitsConfiguration: {
      type: Array,
      default: () => [],
    },
    partnerUnits: {
      type: Array,
    },
    products: {
      type: Array,
      default: () => [],
    },
    itemsFilter: {
      type: Array,
      default: () => [],
    },
    defaultTableSortBy: {
      type: [String, Array],
      default: '',
    },
    defaultTableSortDesc: {
      type: [Boolean, Array],
      default: false,
    },
    resetToDefaultSortWhenEmpty: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    logsDialog: false,
    communicateDialog: false,
    loading: true,
    showAuto: false,
    offerId: '',
    offerScheduleId: '',
    search: '',
    showTooltipTitle: {},
    showTooltipDescription: {},
    DateTime: DateTime,
    items: [],
    headers: [],
    statusToSearch: [],
  }),
  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
    }),

    ...mapGetters(['getSelectedIndustriesIds']),

    query() {
      return QUERY_LIST_SALES_CAMPAIGNS_OF_TABLE
    },
    computedHeaders() {
      const headers = this.headers.filter((header) => header.isActive)
      return headers.map((header) => {
        return {
          ...header,
          width: header.width,
          sortable: header.hasOwnProperty('sortable') ? header.sortable : true,
        }
      })
    },
    allowsViewingFranchiseTradeResults() {
      if (this.$permissions.havePagePermission('campanhas/gestao-ofertas/trade/resultados_franquia')) {
        return true // If is franchisee and have permissions for view franchise's trade results
      }
      return false
    },
    parsedStatus() {
      return this.statusToSearch ? this.statusToSearch : []
    },
    queryVariables() {
      let queryVariables = {
        status: this.statusToSearch,
        franchise: this.franchisee,
        includeAuto: this.showAuto,
        ...(this.units.length
          ? {
              units: this.units,
              units_configuration: this.unitsConfiguration,
              partner_units: this.partnerUnits,
            }
          : {}),
        industry: this.industry,
        products: this.products,
      }

      if (this.industry) {
        if (this.finished) {
          queryVariables.status = this.parsedStatus
        } else {
          queryVariables.status = queryVariables.status.filter((status) => status !== 'draft')
        }
        queryVariables.format = this.stateFilters.mechanics || []
        queryVariables.industryIds = this.getSelectedIndustriesIds

        if (this.stateFilters.mechanics.includes('nextbuycashback')) {
          queryVariables.format = this.stateFilters.mechanics.concat('cashback_absolute')
        }
      }

      if (this.franchisee) {
        const draftPosition = queryVariables.status.indexOf('draft')
        if (draftPosition > -1) queryVariables.status.splice(draftPosition, 1)
      }

      return queryVariables
    },
    industryDefaultHeaders() {
      return [
        {
          text: 'Status',
          value: 'status',
          width: '9.5%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Nome',
          value: 'name',
          width: '16%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Modalidade',
          value: 'modality',
          sortable: false,
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Produtos',
          value: 'products_count',
          width: '7.5%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Formato',
          value: 'types',
          width: '8.5%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Público-alvo',
          value: 'is_personalized',
          width: '11%',
          sortable: false,
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Lojas',
          value: 'units',
          width: '10%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Indústria',
          value: 'industry_name',
          width: '9%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Data de validade',
          value: 'start_date',
          width: '14%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Prazo de adesão',
          value: 'proposal_deadline',
          width: '15%',
          sortable: false,
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Última atualização',
          value: 'updated_at',
          width: '10%',
          sortable: true,
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: '',
          value: 'menu',
          width: '5%',
          sortable: false,
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
      ]
    },
    defaultHeaders() {
      return [
        {
          text: 'Status',
          value: 'status',
          width: '6%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Nome',
          value: 'name',
          width: '14%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Descrição',
          value: 'description',
          width: '10%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Produtos',
          value: 'products_count',
          width: '6%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Formato',
          value: 'types',
          width: '7.5%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Público-alvo',
          value: 'is_personalized',
          width: '10%',
          sortable: false,
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Modelo',
          value: 'activatable',
          width: '7.5%',
          isActive: false,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Lojas',
          value: 'units',
          width: '10%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Data de validade',
          value: 'start_date',
          width: '12%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Data de criação',
          value: 'created_at',
          width: '12%',
          isActive: false,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Última atualização',
          value: 'updated_at',
          width: '10%',
          sortable: true,
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: '',
          value: 'menu',
          width: '5%',
          sortable: false,
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
      ]
    },
    isFranchise() {
      if (!this.$getLoggedUser().is_franchise) return false
      return this.$getLoggedUser().is_franchise
    },
    isFranchisee() {
      if (!this.$getLoggedUser().is_franchisee) return false
      return this.$getLoggedUser().is_franchisee
    },
    isSomeChannelAllowed() {
      const channelsPermissionsStatus = getChannelsPermissionsStatus(this.$permissions, this.$getAllowedModules)
      return checkIfSomeChannelIsAllowed(channelsPermissionsStatus)
    },
    canViewResults() {
      return (item) =>
        this.isZeDeliveryCampaign(item) ||
        (!this.franchisee && item.has_results && item.status === 'finished') ||
        (this.franchisee && (!this.industry || this.allowsViewingFranchiseTradeResults) && (item.status === 'active' || item.status === 'finished'))
    },
    canEditCampaign() {
      return (item) => (!this.franchisee && !this.isZeDeliveryCampaign(item)) || (this.franchisee && item.status === 'scheduled')
    },
  },
  beforeMount() {
    this.statusToSearch = this.itemsFilter.map((item) => item.value)
    if (this.itemsFilter.length > 1) this.statusToSearch.push('processing')
  },
  async mounted() {
    if (this.headers?.length <= 0) {
      this.headers = cloneDeep(this.industry ? this.industryDefaultHeaders : this.defaultHeaders)
    }
    this.$nextTick(() => {
      this.$bus.$on('filter', () => {
        this.$refs.table.waitAndGetData()
      })
    })
  },
  beforeDestroy() {
    this.$bus.$off('filter')
  },

  methods: {
    getStatusLabel,
    getUnits,
    getModality,
    getFilterResult(value) {
      if (value.length === 1 && value[0] === 'processing') value = []
      this.statusToSearch = value.length ? value : this.itemsFilter.map((item) => item.value)
      if (!this.statusToSearch.includes('processing')) this.statusToSearch.push('processing')
    },
    openLogsModal(id) {
      this.offerId = id
      this.logsDialog = true
    },
    openCommunicateModal(id) {
      this.offerScheduleId = id
      this.communicateDialog = true
    },
    getEditRoute(item, franchise) {
      let routeName = this.industry ? 'ofertas-trade-visualizar' : 'gestao-ofertas-editar'
      return { name: routeName, params: { id: item._id }, query: { franchise: franchise } }
    },
    redirectToEdit(item, franchise) {
      const route = this.getEditRoute(item, franchise)
      this.$router.push(route).catch(() => {})
    },
    getResultsRoute(item, franchise) {
      let routeName = this.industry
        ? item.industry.is_mercapromo
          ? 'ofertas-trade-resultado-mercapromo'
          : 'ofertas-trade-resultado'
        : 'gestao-ofertas-resultado'
      return { name: routeName, params: { id: item._id }, query: { franchise: franchise } }
    },
    redirectToResults(item, franchise) {
      const route = this.getResultsRoute(item, franchise)
      this.$router.push(route).catch(() => {})
    },
    redirectToConsumedSalesSession(id, franchise) {
      const routeName = 'gestao-ofertas-historico-consultas'
      this.$router.push({ name: routeName, params: { id: id }, query: { franchise: franchise } }).catch(() => {})
    },
    async cloneCampaign(id, name) {
      const confirmed = await this.$confirm({
        ...this.$t('campanhas.confirmations.clone'),
        message: `Deseja duplicar ${name ? 'a campanha ' + name.trim().bold() : 'esta campanha'}?`,
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return
      this.$refs.table.paginationLoading = true
      await this.$apollo
        .mutate({
          mutation: MUTATION_CLONE_SALES_CAMPAIGN,
          variables: {
            id,
          },
          context: {
            uri: this.$microservicesUrls['sales'],
          },
        })
        .then((response) => {
          const id = response.data.cloneOfferSchedule
          this.$router.push({ name: 'gestao-ofertas-editar', params: { id: id } }).catch(() => {})
          this.$refs.table.waitAndGetData()
        })
        .catch(() => {
          this.$snackbar(this.$t('campanhas.snackbars.errors.clone'))
        })
    },
    async deleteCampaign(id, name) {
      const confirmed = await this.$confirm({
        title: 'Remover campanha',
        message: `Tem certeza que deseja remover a campanha "${name}"?`,
        confirmText: 'Remover',
        confirmColor: 'vermelho-d20',
        cancelText: 'Cancelar',
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return
      this.$refs.table.paginationLoading = true
      await this.$apollo
        .mutate({
          mutation: MUTATION_DELETE_SALES_CAMPAIGN,
          variables: {
            id,
          },
          context: {
            uri: this.$microservicesUrls['sales'],
          },
        })
        .then(() => {
          this.$refs.table.waitAndGetData()
        })
        .catch(() => {
          this.$snackbar(this.$t('campanhas.snackbars.errors.delete'))
        })
      this.$refs.table.paginationLoading = false
    },
    checkCampaignWithSingleSchedule(omni_campaigns) {
      const invalidCampaignStatus = ['FINISHED', 'DRAFT']

      if (!Array.isArray(omni_campaigns) || omni_campaigns.length === 0) {
        return null
      }

      const hasValidCampaign = omni_campaigns.some((omni_campaign) => {
        const hasValidStatus = !invalidCampaignStatus.includes(omni_campaign.status)
        const hasSingleOfferSchedule = omni_campaign.offer_schedules.length === 1

        return hasValidStatus && hasSingleOfferSchedule
      })

      return hasValidCampaign
    },
    async unscheduleCampaign(id, omniCampaigns, industry) {
      let confirmed = false
      const checkResult = this.checkCampaignWithSingleSchedule(omniCampaigns)

      if (checkResult === true && !industry) {
        confirmed = await this.$confirm({
          title: 'Suspender campanha',
          message:
            'A campanha será movida para o status rascunho. As campanhas da maria vinculadas a essa campanha de ofertas também irão ser suspensas, terão seus status alterados para rascunho e não estarão mais vinculadas a essa campanha de ofertas.',
          confirmText: 'Ok',
          cancelText: 'Cancelar',
        })
          .then(() => true)
          .catch(() => false)
      } else if (checkResult === false && !industry) {
        confirmed = await this.$confirm({
          title: 'Suspender campanha',
          message: 'A campanha será movida para o status rascunho. As campanhas da Mar.ia também não estarão mais vinculadas a essa campanha de ofertas.',
          confirmText: 'Ok',
          cancelText: 'Cancelar',
        })
          .then(() => true)
          .catch(() => false)
      } else if (industry && this.isUniqueOfferSchedule && this.proposalDeadlineExpired(industry.proposal_deadline)) {
        confirmed = await this.$confirm({
          ...this.industryFinishWarning('expirada'),
        })
          .then(() => true)
          .catch(() => false)
      } else {
        const statusMsg = industry ? 'proposta indústria' : 'rascunho'
        confirmed = await this.$confirm({
          title: 'Suspender campanha',
          message: `A campanha será movida para o status ${statusMsg}`,
          confirmText: 'Ok',
          cancelText: 'Cancelar',
        })
          .then(() => true)
          .catch(() => false)
      }

      if (!confirmed) return

      this.$refs.table.paginationLoading = true
      await this.$apollo
        .mutate({
          mutation: MUTATION_UNSCHEDULED_SALES_CAMPAIGN,
          variables: {
            id,
          },
          context: {
            uri: this.$microservicesUrls['sales'],
          },
        })
        .then(() => {
          this.$refs.table.waitAndGetData()
        })
        .catch(() => {
          this.$snackbar(this.$t('campanhas.snackbars.errors.unschedule'))
        })
      this.$refs.table.paginationLoading = false
    },

    async finishCampaign(id, omniCampaigns) {
      let confirmed = false
      const checkResult = this.checkCampaignWithSingleSchedule(omniCampaigns)

      if (checkResult === true && !this.industry) {
        confirmed = await this.$confirm({
          title: 'Finalizar campanha',
          message:
            'A campanha será movida para o status concluída. As campanhas da maria vinculadas a essa campanha de ofertas também irão ser finalizadas e terão seus status alterados para concluído.',
          confirmText: 'Ok',
          cancelText: 'Cancelar',
        })
          .then(() => true)
          .catch(() => false)
      } else if (checkResult === false && !this.industry) {
        confirmed = await this.$confirm({
          title: 'Finalizar campanha',
          message:
            'A campanha será movida para o status concluída. As campanhas da Mar.ia vinculadas a essa campanha não realizarão mais disparos com os produtos dessa oferta.',
          confirmText: 'Ok',
          cancelText: 'Cancelar',
        })
          .then(() => true)
          .catch(() => false)
      } else if (this.industry) {
        confirmed = await this.$confirm({
          ...this.industryFinishWarning('concluída'),
        })
          .then(() => true)
          .catch(() => false)
      } else {
        confirmed = await this.$confirm({
          title: 'Finalizar campanha',
          message: 'A campanha será movida para o status concluída',
          confirmText: 'Ok',
          cancelText: 'Cancelar',
        })
          .then(() => true)
          .catch(() => false)
      }

      if (!confirmed) return

      this.$refs.table.paginationLoading = true
      await this.$apollo
        .mutate({
          mutation: MUTATION_FINISH_SALES_CAMPAIGN,
          variables: {
            id,
          },
          context: {
            uri: this.$microservicesUrls['sales'],
          },
        })
        .then(() => {
          this.$refs.table.waitAndGetData()
        })
        .catch((error) => {
          console.error(error)
          this.$snackbar(this.$t('campanhas.snackbars.errors.unschedule'))
        })
      this.$refs.table.paginationLoading = false
    },

    getCampaignDuration(item) {
      if (item.status === 'active') {
        const { days: endCampaign } = DateTime.fromISO(item.end_date).diffNow(['days']).toObject()
        return `A campanha terminará em ${parseInt(endCampaign)} dia(s)`
      } else if (item.status === 'scheduled') {
        const { days: initCampaign } = DateTime.fromISO(item.start_date).diffNow(['days']).toObject()
        return `A campanha iniciará em ${parseInt(initCampaign)} dia(s)`
      }
    },
    hasResults(status) {
      return ['ACTIVE', 'FINISHED'].includes(status)
    },
    getMessageDates(date, time = null) {
      let messageData = ''
      let dataFormater = ''
      if (time) {
        messageData = ''.concat(date, ' ', time)
        dataFormater = 'DD/MM/YYYY | HH:mm'
      } else {
        messageData = date
        dataFormater = 'DD/MM/YYYY'
      }
      return date ? DateTime.fromISO(messageData).toFormat(dataFormater) : '-'
    },
    showTooltip(status) {
      return status === 'active' || status === 'scheduled'
    },
    proposalDeadlineExpired(proposal_deadline) {
      return DateTime.now() > DateTime.fromISO(proposal_deadline).endOf('day')
    },
    getOfferTypes(types) {
      const offerTypes = []
      types.forEach((type) => offerTypes.push(getProductFormatTypeLabel(type)))
      return offerTypes
    },
    getOfferTypesTooltip(types) {
      return this.getOfferTypes(types).join(', ')
    },
    getDefaultHeadersOptions() {
      return cloneDeep(this.industry ? this.industryDefaultHeaders : this.defaultHeaders)
    },
    verifyHeaderProperty(property) {
      let exists = false
      const headers = this.computedHeaders
      headers.map((item) => {
        item.value === property && (exists = true)
      })
      return exists
    },
    isZeDeliveryCampaign(item) {
      return item.types[0] === 'benefits'
    },
  },
}
</script>

<style lang="scss" scoped>
.card-title {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-left: 0;
  padding-bottom: 16px;
}

.min-width-345 {
  min-width: 345px;
}

.status-search-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
}

.history-striped {
  tr:nth-child(even) {
    background-color: #f7f7f7;
  }
}

.text-align-right {
  text-align: right;
  margin-right: 12px;
}

.colorful-border {
  border-left: 3px solid;
  height: 100%;
}

tr td:not(:first-child) .colorful-border {
  border: none;
}

::v-deep td {
  padding: 0 !important;
}

.margin-left-btn {
  margin-left: 12px;
}

.primary-color {
  background-color: $marca-0;
}

.unset-capitalize {
  text-transform: unset !important;
  letter-spacing: $letterspacing-1;
}

.fix-td-position {
  padding: 0 16px;
  display: flex;
  align-items: center;
}

.modality-chip {
  width: 95px;
  text-align: center;
  display: flex;
  justify-content: center;
}

@media screen and (max-width: 1366px) {
  .link {
    font-size: 12px !important;
  }
}
</style>
